import { placeInMapsLink } from '../functions/lib/maps-link'
import { addressLine } from './address-words'
// The day's round on a map — the pure half.
//
// One tap on a day heading opens Google Maps with that day's stops as a
// route, in the order the diary has them, so the owner can see the streets
// and how far apart they are and then move the visits around (Liam,
// 2026-09-14: "see what street they are and how close they are, if we need to
// adjust the times"). Roundly draws no map of its own and stores no map
// position: nothing here needs a maps account, a key or a penny.

// Google's own limit on the `dir` URL: an origin, a destination, and nine
// waypoints between them. A day with more stops than this opens as the first
// eleven rather than failing — the alternative is no map at all, and the
// eleventh stop is still enough to see the shape of the day.
export const MAX_STOPS = 11

export interface MapStop {
  customer_name?: string | null
  customer_address?: string | null
  customer_postcode?: string | null
  // Their exact spot when pinned (functions/lib/maps-link.ts): the route goes to it, and its dot sits on it.
  customer_maps_link?: string | null
}

// What Google is asked to find: the address and the postcode together. The
// postcode alone lands on the middle of a street and the address alone is
// ambiguous across London, so a stop with neither is not a stop that can be
// pinned and is dropped by stopsOf below.
export function addressOf(cl: MapStop): string {
  return addressLine(cl.customer_address, cl.customer_postcode, ', ')
}

// The visits that can go on the map, in the order they were given. The caller
// filters to the day's real visits first (no suggestions — the van does not
// drive to a machine's proposal); this only drops the ones with nowhere to go.
export function stopsOf(rows: MapStop[]): string[] {
  return rows.map((r) => { const s = placeInMapsLink(r.customer_maps_link); return s ? `${s.lat},${s.lng}` : addressOf(r) }).filter((a) => a.length > 0)
}

// The link itself, or null when there is nothing to show.
//
// One stop is a search, not a route: `dir` with a single address gives
// directions from wherever Google thinks you are, which is not what a single
// pin on a street is for.
export function mapsHref(rows: MapStop[]): string | null {
  const stops = stopsOf(rows).slice(0, MAX_STOPS)
  if (!stops.length) return null
  const q = (s: string): string => encodeURIComponent(s)
  if (stops.length === 1) return `https://www.google.com/maps/search/?api=1&query=${q(stops[0]!)}`
  const origin = stops[0]!
  const destination = stops[stops.length - 1]!
  const between = stops.slice(1, -1)
  const waypoints = between.length ? `&waypoints=${between.map(q).join('%7C')}` : ''
  return `https://www.google.com/maps/dir/?api=1&origin=${q(origin)}&destination=${q(destination)}${waypoints}&travelmode=driving`
}

// What the button says it will do, for the tooltip and the screen reader.
// Names the count, and says so plainly when the day is longer than one link
// can carry rather than letting stops disappear without a word.
export function mapLabel(rows: MapStop[], dayWords: string): string {
  const n = stopsOf(rows).length
  if (n === 0) return `No addresses to map on ${dayWords}`
  if (n === 1) return `Map the one stop on ${dayWords}`
  if (n <= MAX_STOPS) return `Map all ${n} stops on ${dayWords}`
  return `Map the first ${MAX_STOPS} of ${n} stops on ${dayWords}`
}


// ---- the day's own map -------------------------------------------------------
//
// Google Maps drops ONE pin per link and nothing more: two or more addresses
// and it offers a route instead of showing you where people are (Liam,
// 2026-09-14, after the route link shipped: "it's trying to plan a route
// between them all ... it's also not showing the pins"). So the pins are
// drawn here, from each customer's own position (migration 0014).

export interface PinStop {
  id: number
  customer_id: number
  customer_name?: string | null
  customer_address?: string | null
  customer_postcode?: string | null
  // The postcode Roundly last looked up for the map (place-on-map.ts): the same as customer_postcode with no position
  // means it was tried and not found (a half postcode).
  customer_geocoded_postcode?: string | null
  customer_lat?: number | null
  customer_lng?: number | null
  customer_maps_link?: string | null
  time_slot?: string | null
  state?: string
  blocked_reason?: string | null
  // The number the day's list shows beside this stop, when the caller has it (the Diary's Day: its filters can hide
  // stops, and the map must say the same numbers as the list); null for one the list leaves unnumbered.
  n?: number | null
  // What the dot says instead of its number (Today on a computer: another person's stops, D1, D2), and a job under way
  // (its dot goes dark). Both optional; the Diary passes neither.
  label?: string | null
  live?: boolean
}

export interface Pin {
  n: number | null     // its number in the day's list, which is what the dot shows; null for a visit already done
  id: number
  customerId: number
  name: string
  address: string
  lat: number
  lng: number
  done: boolean
  blocked: boolean     // "Couldn't do it": grey, still numbered
  label?: string | null
  live?: boolean
  // Their Map link, so Directions go to an exact spot when one is pinned (maps-link.ts).
  link?: string | null
}

// The stops that can be drawn, numbered by their place in the WHOLE day —
// so a dot labelled 3 is the third visit even when the first two have no
// position and no dot. Renumbering the drawable ones would quietly disagree
// with the list of visits right above the map. The Diary numbers only the
// visits still to do, and passes its own numbers (`n`, null for a done
// visit); everywhere else (the phone, the map's own tab) every stop is
// numbered by its place, as before (Astra, 24 Oct 2026).
function numbersOf(rows: PinStop[]): (number | null)[] {
  return rows.map((r, i) => (r.n !== undefined ? r.n : i + 1))
}
export function pinsOf(rows: PinStop[]): Pin[] {
  const pins: Pin[] = []
  const numbers = numbersOf(rows)
  rows.forEach((r, i) => {
    // The pinned door first; else the postcode's position (0014).
    const spot = placeInMapsLink(r.customer_maps_link)
    const lat = spot ? spot.lat : typeof r.customer_lat === 'number' ? r.customer_lat : null
    const lng = spot ? spot.lng : typeof r.customer_lng === 'number' ? r.customer_lng : null
    if (lat === null || lng === null) return
    pins.push({
      n: numbers[i] ?? null,
      id: r.id,
      customerId: r.customer_id,
      name: r.customer_name ?? `#${r.customer_id}`,
      address: addressOf(r),
      lat,
      lng,
      done: r.state === 'done',
      blocked: !!r.blocked_reason,
      ...(r.label ? { label: r.label } : {}),
      ...(r.live ? { live: true } : {}),
      ...(r.customer_maps_link ? { link: r.customer_maps_link } : {}),
    })
  })
  return pins
}

// The stops that have to be named in words because there is no dot for them.
// Why someone is not on the map, in his words (Liam, 26 Oct 2026: "sarah has a postcode so dont understand the message"):
// no postcode; one Roundly looked up and couldn't place (half of one, "N10"); or one it is placing now (place-on-map.ts,
// after the Diary, Today or Work is read).
export function notOnMapWhy(r: PinStop): string {
  const pc = String(r.customer_postcode ?? '').trim()
  if (!pc) return 'No postcode yet'
  const tried = String(r.customer_geocoded_postcode ?? '').trim().toUpperCase() === pc.toUpperCase()
  return tried ? `Roundly couldn't find ${pc}: add their full postcode` : `Going on the map now from ${pc}`
}
export function unmappable(rows: PinStop[]): { n: number | string | null; name: string; customerId: number; why: string }[] {
  const numbers = numbersOf(rows)
  const seen = new Set<number>()
  return rows
    .map((r, i) => ({ n: r.label || (numbers[i] ?? null), name: r.customer_name ?? `#${r.customer_id}`, customerId: r.customer_id, why: notOnMapWhy(r), ok: !!placeInMapsLink(r.customer_maps_link) || (typeof r.customer_lat === 'number' && typeof r.customer_lng === 'number') }))
    .filter((r) => !r.ok && !seen.has(r.customerId) && seen.add(r.customerId))
    .map(({ n, name, customerId, why }) => ({ n, name, customerId, why }))
}

// The box the map has to show to fit every dot, with a little air around it.
// One dot has no width, so it gets a fixed margin instead of a proportional
// one: a zero-size box asked Leaflet to zoom to the maximum and landed on a
// single roof.
export function boundsOf(pins: Pin[]): { south: number; west: number; north: number; east: number } | null {
  if (!pins.length) return null
  const lats = pins.map((p) => p.lat)
  const lngs = pins.map((p) => p.lng)
  const south = Math.min(...lats), north = Math.max(...lats)
  const west = Math.min(...lngs), east = Math.max(...lngs)
  const padLat = Math.max((north - south) * 0.25, 0.004)   // ~450m
  const padLng = Math.max((east - west) * 0.25, 0.006)
  return { south: south - padLat, west: west - padLng, north: north + padLat, east: east + padLng }
}

// How far apart the two furthest stops are, in a straight line, in miles —
// the "how close are they" answer in one phrase, under the map.
export function spreadMiles(pins: Pin[]): number {
  if (pins.length < 2) return 0
  let worst = 0
  for (let i = 0; i < pins.length; i++) {
    for (let j = i + 1; j < pins.length; j++) {
      const d = haversineMiles(pins[i]!, pins[j]!)
      if (d > worst) worst = d
    }
  }
  return worst
}

function haversineMiles(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 3958.8
  const toRad = (d: number): number => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)))
}

// "4 stops, furthest two 1.2 miles apart" — or the honest short forms.
export function spreadWords(pins: Pin[]): string {
  const n = pins.length
  if (n === 0) return 'Nothing to show on the map'
  if (n === 1) return '1 stop'
  const miles = spreadMiles(pins)
  const how = miles < 0.1 ? 'all within a few doors' : `furthest two ${miles.toFixed(miles < 10 ? 1 : 0)} miles apart`
  return `${n} stops, ${how}`
}
